import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectStudentPath,
  tryStudentCoursesPath,
} from "../../../store/slices/student-courses";
import {
  selectStudentAttendance,
  tryGetStudentAttendance,
} from "../../../store/slices/student-courses-attendances";

import {
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  Typography,
  Box,
  List,
  ListItem
} from "@mui/material";
import ModalMessages from "../../layout/modal-messages";
import CustomColumnMenu from "../../layout/grid-column-menu";
import Spinner from "../../layout/spinner";
import moment from "moment";
import { DataGrid, GridCloseIcon } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const StudentPathDrawer = ({
  openPathDrawer,
  setOpenPathDrawer,
  studentId,
  courseId,
  title,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const studentPath = useSelector(selectStudentPath);
  const attendanceTable = useSelector(selectStudentAttendance);
  const [rows, setRows] = useState([]);

  const model = {
    studentId: studentId,
    courseId: courseId,
  };

  //Verifica si se muestra el record de asistencia
  const perfectAssistance = () => {
    if (!studentPath || !studentPath.summary) return "";

    let valuesSplitted = studentPath.summary.split("/");
    let result = valuesSplitted[0] / valuesSplitted[1];
    if (result === 1) return "Record de Asistencia";
    return "";
  };

  //beforeToday false indica que NO se filtren las clases hasta el dia de hoy
  const beforeToday = false;
  useEffect(() => {
    dispatch(tryStudentCoursesPath(model));
    dispatch(tryGetStudentAttendance({ model, beforeToday }));
  }, []);

  useEffect(() => {
    if (attendanceTable.data) {
      setRows(
        attendanceTable.data.map((x) => {
          return {
            id: x.attendanceId,
            attendanceId: x.attendanceId,
            studentCourseId: x.studentCourseId,
            attendanceDate: x.attendanceDate,
            studentValid: x.studentValid ?? false,
            teacherValid: x.teacherValid ?? false,
          };
        })
      );
    }
  }, [attendanceTable.data]);

  const initializeDataGrid = () => {
    return {
      ...rows,
      columns: {
        ...columns,
        columnVisibilityModel: {
          id: false,
        },
      },
      pagination: { paginationModel: { pageSize: 6 } },
    };
  };

  const columns = [
    { field: "id", headerName: "Id", filterable: false },
    {
      field: "attendanceDate",
      headerAlign: "left",
      align: "left",
      flex: 0.3,
      headerName: "Cronograma de Clases",
      headerClassName: "headRow",
      width: 100,

      valueFormatter: (params) => {
        if (params.value === null) return "";
        return moment(params.value).format("ll");
      },
    },
  ];

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={openPathDrawer}
      onClose={() => setOpenPathDrawer(false)}
      PaperProps={{
        sx: {
          zIndex: 1203,
          width: { xs: "100vw", sm: 500 },
          maxWidth: "100%",
          maxHeight: { xs: "90vh", sm: "100%" },
          borderRadius: { xs: "12px 12px 0 0", sm: 0 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
      sx={{
        zIndex: 1203,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: "#194B6F",
          color: "white",
          position: "relative",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 4 },
          pb: { xs: 1.5, sm: 3 },
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            color: "white",
          }}
          onClick={() => setOpenPathDrawer(false)}
        >
          <GridCloseIcon />
        </IconButton>

        <Typography
          align="left"
          sx={{
            fontWeight: 700,
            fontSize: 18,
            mb: { xs: 0, sm: 1 },
            pr: 4,
          }}
        >
          Mi Trayectoria
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          backgroundColor: "white",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Grid container rowSpacing={3} spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}>
            <Typography
              style={{ color: "black", fontWeight: "bold", fontSize: 18 }}
            >
              {title}
            </Typography>
          </Grid>
          <Grid item xs={12} marginTop={-3}>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{ bgcolor: "secondary.light" }}
            />
          </Grid>

          {studentPath && (
            <>
              <Grid item xs={12}>
                <Typography style={{ fontWeight: "bold", color: "black" }}>
                  Última clase:{" "}
                  {studentPath.lastEdition
                    ? moment(studentPath.lastEdition).format("ll")
                    : "Sin información"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography style={{ fontWeight: "bold", color: "black" }}>
                  Próxima cursada:{" "}
                  {studentPath.nextToStudy
                    ? moment(studentPath.nextToStudy).format("ll")
                    : "Sin información"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography style={{ fontWeight: "bold", color: "black" }}>
                  Clases hasta la fecha: {studentPath.classesTaught ?? "0"} Total
                  de Clases: {studentPath.totalClasses ?? "0"}
                </Typography>
                <Typography style={{ color: "black" }}>
                  <strong>{studentPath.summary ?? "0"}</strong> clases completadas
                  y clases impartidas{" "}
                  {studentPath.studentConditionCoursesDescription !== "" && (
                    <Chip
                      label={studentPath.studentConditionCoursesDescription}
                      color={
                        studentPath.conditioningType === 1 ? "success" : "error"
                      }
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography style={{ fontWeight: "bold", color: "black" }}>
                  Porcentaje de asistencia: % {studentPath.progress}
                </Typography>
                <LinearProgress
                  sx={{
                    fontSize: 60,
                    width: "100%",
                    maxWidth: "400px",
                    height: "10px",
                    mt: 1,
                  }}
                  variant="determinate"
                  value={studentPath.progress}
                />
                <Typography sx={{ color: "grey", mt: 0.5 }}>
                  {perfectAssistance()}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {isMobile ? (
                  <>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ px: 1, pb: 1, borderBottom: '1px solid #e0e0e0' }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: 'text.secondary' }}>
                        Cronograma de Clases
                      </Typography>
                    </Box>
                    <List disablePadding>
                      {rows.map((row, index) => (
                        <React.Fragment key={row.id}>
                          <ListItem
                            disableGutters
                            sx={{ py: 1.5, px: 1 }}
                          >
                            <Typography color="text.primary" sx={{ fontSize: 15 }}>
                              {moment(row.attendanceDate).format("ll")}
                            </Typography>
                          </ListItem>
                          {index < rows.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                      {rows.length === 0 && (
                        <Typography color="textSecondary" align="center" sx={{ mt: 2 }}>No hay clases registradas.</Typography>
                      )}
                    </List>
                  </>
                ) : (
                  <DataGrid
                    slots={{
                      columnMenu: CustomColumnMenu,
                    }}
                    autoHeight={true}
                    rowSelection={false}
                    getRowId={(row) => row.id}
                    rows={rows}
                    columns={columns}
                    rowsPerPageOptions={[5]}
                    initialState={initializeDataGrid()}
                  />
                )}
              </Grid>
              <Grid item sx={{ width: "100%", textAlign: "center" }}>
                <Button
                  variant="contained"
                  onClick={() => setOpenPathDrawer(false)}
                  disabled={studentPath.isLoading}
                  fullWidth={isMobile}
                  sx={{
                    mt: { xs: 1, md: 2 },
                    mb: 2,
                    width: { xs: "100%", md: 100 },
                  }}
                >
                  {studentPath.isLoading && <Spinner color="primary" />}
                  ACEPTAR
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
      {studentPath.errors && studentPath.errors.length > 0 && (
        <ModalMessages errors={studentPath.errors} />
      )}
    </Drawer>
  );
};

export default StudentPathDrawer;